import dayjs from "dayjs";
import { useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import type { ReactDatePickerCustomHeaderProps, ReactDatePickerProps } from "react-datepicker";

import ArrowLeftIcon from "@assets/icons/arrow-left.svg";
import ArrowRightIcon from "@assets/icons/arrow-right.svg";
import CalendarIcon from "@assets/icons/calendar.svg";
import { useScrollLock } from "@hooks/useScrollLock";
import { cn } from "@utils/tailwind.util";

import { Input, InputProps } from "./Input";

const CALENDAR_CLASS = cn(
  "!rounded-xl !border !font-inter !shadow-lg",
  "!border-stroke-primary !bg-white dark:!border-gray-600 dark:!bg-gray-850"
);

const DAY_CLASS = cn(
  "!rounded-lg !font-normal !text-content-primary dark:!text-white",
  "hover:!bg-action-secondaryHover dark:hover:!bg-gray-700"
);

/** Today (when nothing is picked) and the selected day, both highlighted as in the CRM. */
const HIGHLIGHTED_DAY_CLASS =
  "!bg-action-danger !font-medium !text-white hover:!bg-action-dangerHover";

const WEEK_DAY_CLASS = "!font-medium !text-content-secondary dark:!text-invertedPrimary80";

/**
 * Above the forms' sticky footer (z-[1]). A dropdown menu can shrink to the space left above it,
 * but the calendar is a fixed-size grid — without this the footer cuts it in half.
 */
const POPPER_CLASS = "!z-[2]";

export interface DatePickerProps extends Omit<ReactDatePickerProps, "onChange"> {
  selectedDate?: Date | null;
  onChange: (date: Date | null) => void;
  showIcon?: boolean;
  inputProps?: InputProps;
}

/**
 * Single-date picker, ported from the CRM so both apps share the calendar's look and behaviour:
 * custom month header, Monday-first weeks, and argo's `Input` as the trigger.
 */
export function DatePicker({
  selectedDate,
  onChange,
  showIcon = true,
  inputProps,
  popperClassName,
  onCalendarOpen,
  onCalendarClose,
  ...rest
}: DatePickerProps) {
  const datePickerRef = useRef<ReactDatePicker>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useScrollLock({ enabled: isCalendarOpen, containerRef });

  // react-datepicker's own --selected/--today classes can't be targeted from a className prop,
  // so the highlight is decided here instead.
  const isHighlighted = (date: Date) =>
    selectedDate ? dayjs(date).isSame(selectedDate, "day") : dayjs(date).isSame(dayjs(), "day");

  const calendarIcon = (
    <button
      type="button"
      className="flex"
      aria-label="Open calendar"
      onClick={() => datePickerRef.current?.setFocus()}
    >
      <CalendarIcon width={20} height={20} className="text-content-disabled" />
    </button>
  );

  const renderCustomHeader = ({
    monthDate,
    decreaseMonth,
    increaseMonth,
  }: ReactDatePickerCustomHeaderProps) => (
    <div className="mb-1 flex h-[26px] w-full items-center justify-between text-content-primary dark:text-white">
      <button type="button" className="p-2" aria-label="Previous month" onClick={decreaseMonth}>
        <ArrowLeftIcon width={16} height={16} />
      </button>
      <span className="text-[13px] font-medium">
        {monthDate.toLocaleString("default", { month: "long" })} {monthDate.getFullYear()}
      </span>
      <button type="button" className="p-2" aria-label="Next month" onClick={increaseMonth}>
        <ArrowRightIcon width={16} height={16} />
      </button>
    </div>
  );

  return (
    <div ref={containerRef} className="w-full">
      <ReactDatePicker
        ref={datePickerRef}
        selected={selectedDate}
        onChange={onChange}
        customInput={
          <Input
            isFullWidth
            className="text-sm"
            EndIcon={showIcon ? calendarIcon : undefined}
            {...inputProps}
          />
        }
        showPopperArrow={false}
        popperClassName={cn(POPPER_CLASS, popperClassName)}
        calendarClassName={CALENDAR_CLASS}
        dayClassName={(date) => cn(DAY_CLASS, isHighlighted(date) && HIGHLIGHTED_DAY_CLASS)}
        weekDayClassName={() => WEEK_DAY_CLASS}
        renderCustomHeader={renderCustomHeader}
        calendarStartDay={1} // Start calendar on Monday
        wrapperClassName="w-full"
        onCalendarOpen={() => {
          setIsCalendarOpen(true);
          onCalendarOpen?.();
        }}
        onCalendarClose={() => {
          setIsCalendarOpen(false);
          onCalendarClose?.();
        }}
        {...rest}
      />
    </div>
  );
}
